import { CommonModule, formatDate } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  Input,
  OnInit,
  OnDestroy,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { KycAccordionComponent } from '@shared/ui/kyc-accordion/kyc-accordion.component';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
} from '@angular/forms';
import { InputLabelComponent } from '@shared/ui/input-label/input-label.component';
import { InputComponent } from '@shared/ui/input/input.component';
import {
  ButtonDirective,
  SpinDirective,
} from '@shared/directives/button.directive';
import { BaseSelectDirective } from '@shared/directives/base-select.directive';
import { InputDateDirective } from '@shared/directives/input-date.directive';
import { TabsComponent } from '@shared/ui/tabs/tabs.component';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { MEANS_OF_ID } from 'app/app.constants';
import { ActivatedRoute } from '@angular/router';
import { NotificationFacade } from '@core/facades/notification.facade';
import { Jointkyc } from '@core/models/admin/account/joint';
import { JointRegistrationService } from '@core/services/joint-registration.service';
import { ReportService } from '@core/services/report.service';
import { COUNTRIES } from '@core/data/countries-states';

interface ProfileFormState {
  form: FormGroup;
  isLoading: BehaviorSubject<boolean>;
  hasId: BehaviorSubject<boolean>;
}

@Component({
  selector: 'app-edit-joint-means-of-id',
  standalone: true,
  imports: [
    CommonModule,
    KycAccordionComponent,
    ReactiveFormsModule,
    InputLabelComponent,
    InputComponent,
    ButtonDirective,
    BaseSelectDirective,
    InputDateDirective,
    TabsComponent,
    SpinDirective,
  ],
  templateUrl: './edit-joint-means-of-id.component.html',
  styleUrl: './edit-joint-means-of-id.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EditJointMeansOfIdComponent implements OnInit, OnDestroy {
  @Input() jointDataDetails!: Jointkyc[];

  @ViewChild('profileTemplate', { static: true })
  profileTemplate!: TemplateRef<any>;
  
  profileTabs: { label: string; contentTemplate: TemplateRef<any>; context?: any }[] = [];
  countries = COUNTRIES.data;
  accountId!: string;
  subs: Subscription[] = [];
  meansOfIdTypes: any[] = [];
  requiresExpiry = true;

  accountIdSubject$ = new BehaviorSubject<string | null>(null);
  
  // Dynamic profile form states
  profileFormStates: ProfileFormState[] = [];
  profileObservables$: Observable<boolean>[] = [];

  private fb = inject(FormBuilder);
  private route = inject(ActivatedRoute);
  private toast = inject(NotificationFacade);
  private jointRegService = inject(JointRegistrationService);
  private reportService = inject(ReportService);
  private cdr = inject(ChangeDetectorRef);

  ngOnInit(): void {
    this.getAllEnums();
    this.accountId = this.route.snapshot.parent?.params['accountId'];
    if (this.accountId) {
      this.accountIdSubject$.next(this.accountId);
    }

    // Initialize profile form states dynamically
    if (this.jointDataDetails?.length) {
      this.jointDataDetails.forEach((profile, index) => {
        const formState: ProfileFormState = {
          form: this.createProfileForm(),
          isLoading: new BehaviorSubject<boolean>(false),
          hasId: new BehaviorSubject<boolean>(false),
        };
        this.profileFormStates.push(formState);
        this.profileObservables$.push(formState.isLoading.asObservable());
      });

      this.buildProfileTabs();
      this.setFormValues();
      this.setupFormValueChanges();
    }
  }

  private buildProfileTabs(): void {
    this.profileTabs = this.jointDataDetails.map((profile, index) => {
      const fullName = `${profile.personal_details.first_name} ${profile.personal_details.last_name}`.trim();
      return {
        label: fullName || `Profile ${index + 1}`,
        contentTemplate: this.profileTemplate,
        context: { profileIndex: index }
      };
    });
  }

  getAllEnums() {
    const sub = this.reportService.getAllEnums().subscribe({
      next: (response) => {
        this.meansOfIdTypes = this.convertApiToDropdown(
          response?.data?.MeansOfIdentification
        );
        this.cdr.detectChanges();
      },
      error: (resp) => {
        this.cdr.detectChanges();
      },
    });
    this.subs.push(sub);
  }

  convertApiToDropdown(apiData: string[]): { value: string; label: string }[] {
    return apiData.map((item) => ({
      value: item,
      label: item,
    }));
  }

  submitForm(profileIndex: number): void {
    if (profileIndex >= this.profileFormStates.length) return;

    const state = this.profileFormStates[profileIndex];
    state.hasId.value
      ? this.updateMeansOfId(profileIndex)
      : this.addMeansOfId(profileIndex);
  }

  setupFormValueChanges(): void {
    this.profileFormStates.forEach((state) => {
      const sub = state.form.get('type')?.valueChanges.subscribe((val) => {
        this.requiresExpiry = val === 'nin' ? false : true;
      });
      if (sub) this.subs.push(sub);
    });
  }

  updateMeansOfId(profileIndex: number): void {
    const state = this.profileFormStates[profileIndex];
    state.isLoading.next(true);
    
    const formValue = state.form.getRawValue();
    formValue.account_id && state.form.removeControl('account_id');
    formValue.joint_account_id && state.form.removeControl('joint_account_id');

    const sub = this.jointRegService.updateMeansOfID(formValue).subscribe({
      next: () => {
        state.isLoading.next(false);
        this.toast.success('Successfully updated Means of Identification');
      },
      error: () => {
        state.isLoading.next(false);
        this.toast.error('Failed to update Means of Identification');
      },
    });
    this.subs.push(sub);
  }

  addMeansOfId(profileIndex: number): void {
    const state = this.profileFormStates[profileIndex];
    state.isLoading.next(true);

    const formValue = state.form.getRawValue();

    const sub = this.jointRegService.addMeansOfId(formValue).subscribe({
      next: (response: any) => {
        state.hasId.next(true);
        state.isLoading.next(false);
        state.form.addControl('id', new FormControl(response.data.id));
        state.form.removeControl('account_id');
        state.form.removeControl('joint_account_id');
        this.toast.success('Successfully added Means of Identification');
      },
      error: (resp) => {
        state.isLoading.next(false);
        this.toast.error(
          resp?.error?.message || 'Failed to add means of identification details'
        );
      },
    });
    this.subs.push(sub);
  }

  private createProfileForm(): FormGroup {
    return this.fb.nonNullable.group({
      type: [''],
      number: [''],
      issue_date: [new Date()],
      expiry_date: [new Date()],
      account_id: [''],
      joint_account_id: [''],
    });
  }

  setFormValues() {
    this.jointDataDetails.forEach((profile, index) => {
      if (index < this.profileFormStates.length) {
        const state = this.profileFormStates[index];
        const { id_kyc, id } = profile;

        if (!id_kyc) {
          state.form.patchValue({
            account_id: this.accountIdSubject$.value,
            joint_account_id: id,
          });
          return;
        }

        // Has means of ID
        state.hasId.next(true);

        state.form.addControl('id', new FormControl(id_kyc.id));
        state.form.removeControl('account_id');
        state.form.removeControl('joint_account_id');

        state.form.patchValue({
          type: id_kyc.type,
          number: id_kyc.number,
        });

        if (id_kyc.issue_date) {
          state.form.controls['issue_date'].setValue(
            formatDate(id_kyc.issue_date, 'yyyy-MM-dd', 'en')
          );
        }

        if (id_kyc.expiry_date) {
          state.form.controls['expiry_date'].setValue(
            formatDate(id_kyc.expiry_date, 'yyyy-MM-dd', 'en')
          );
        }
      }
    });
  }

  ngOnDestroy(): void {
    this.subs.forEach((sub) => sub.unsubscribe());
  }
}
